import { MobileOfferCatalogEntry, MobileOfferStatus, MobileOfferStoreStatus } from '@numeo/types'
import { MobilePlan } from '@numeo/types-core'
import { Search } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { mobileOfferError, mobileOffersApi } from './api'
import { customerOfferLabel, customerOfferSecondaryClass, customerPlanName, customerStoreName } from './customer-offer-utils'
import { OfferButton, offerFieldClass, OfferNotice } from './ui'
import { assignmentStores, AssignmentStoreSelection } from './assignmentStores'

interface CompatibleOfferPickerProps {
    plan: MobilePlan
    provider: AssignmentStoreSelection
    selected: MobileOfferCatalogEntry | null
    onSelect: (offer: MobileOfferCatalogEntry) => void
}

export function CompatibleOfferPicker({ plan, provider, selected, onSelect }: CompatibleOfferPickerProps) {
    const [search, setSearch] = useState('')
    const [offers, setOffers] = useState<MobileOfferCatalogEntry[]>([])
    const [nextCursor, setNextCursor] = useState<string | null>(null)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')
    const [reload, setReload] = useState(0)
    const sequence = useRef(0)
    const invalidateRequests = useCallback(() => {
        sequence.current++
    }, [])

    useEffect(() => {
        const request = ++sequence.current
        setOffers([])
        setNextCursor(null)
        setBusy(true)
        setError('')
        const timer = window.setTimeout(
            () => {
                void mobileOffersApi
                    .list({
                        plan,
                        provider: provider === 'both' ? undefined : provider,
                        status: MobileOfferStatus.ACTIVE,
                        search: search.trim() || undefined,
                        limit: 20,
                    })
                    .then((page) => {
                        if (request !== sequence.current) return
                        setOffers(page.items)
                        setNextCursor(page.nextCursor)
                    })
                    .catch((failure) => {
                        if (request === sequence.current) setError(mobileOfferError(failure))
                    })
                    .finally(() => {
                        if (request === sequence.current) setBusy(false)
                    })
            },
            search ? 250 : 0
        )
        return () => {
            window.clearTimeout(timer)
            invalidateRequests()
        }
    }, [plan, provider, search, reload, invalidateRequests])

    const loadMore = async () => {
        if (!nextCursor || busy) return
        const request = sequence.current
        setBusy(true)
        setError('')
        try {
            const page = await mobileOffersApi.list({
                cursor: nextCursor,
                plan,
                provider: provider === 'both' ? undefined : provider,
                status: MobileOfferStatus.ACTIVE,
                search: search.trim() || undefined,
                limit: 20,
            })
            if (request !== sequence.current) return
            setOffers((current) => [...current, ...page.items.filter((offer) => !current.some((item) => item.id === offer.id))])
            setNextCursor(page.nextCursor)
        } catch (failure) {
            if (request === sequence.current) setError(mobileOfferError(failure))
        } finally {
            if (request === sequence.current) setBusy(false)
        }
    }

    const compatible = offers.filter(
        (offer) =>
            offer.terms.plan === plan &&
            offer.status === MobileOfferStatus.ACTIVE &&
            assignmentStores(provider).every((store) => offer.stores[store].status === MobileOfferStoreStatus.READY && offer.stores[store].prices?.length)
    )
    return (
        <div className="space-y-3">
            {selected && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700 dark:border-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                    <span className="font-semibold">Selected: </span>
                    {customerOfferLabel(selected)}
                </div>
            )}
            <details className="rounded-lg border border-slate-200 dark:border-slate-700" open={!selected}>
                <summary className="cursor-pointer px-3 py-3 text-sm font-medium focus-visible:ring-2 focus-visible:ring-blue-600">
                    {selected ? 'Change offer' : 'Choose a verified offer'}
                </summary>
                <div className="space-y-3 border-t border-slate-200 p-3 dark:border-slate-700">
                    <label className="flex items-center gap-2">
                        <Search className="h-4 w-4 shrink-0 text-slate-500 dark:text-slate-400" />
                        <input
                            aria-label="Search compatible offers"
                            maxLength={100}
                            className={offerFieldClass}
                            value={search}
                            placeholder="Search Gluon offers"
                            onChange={(event) => {
                                sequence.current++
                                setOffers([])
                                setNextCursor(null)
                                setBusy(true)
                                setSearch(event.target.value)
                            }}
                        />
                    </label>
                    <div className="max-h-72 space-y-2 overflow-y-auto" aria-busy={busy}>
                        {compatible.map((offer) => (
                            <button
                                key={offer.id}
                                type="button"
                                onClick={() => onSelect(offer)}
                                aria-pressed={selected?.id === offer.id}
                                className="block w-full rounded-lg border border-slate-200 p-3 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 dark:border-slate-700 dark:hover:bg-slate-800"
                            >
                                <span className="block text-sm font-medium">{customerOfferLabel(offer)}</span>
                                <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">
                                    {offer.terms.countries.slice(0, 3).join(', ')}
                                    {offer.terms.countries.length > 3 ? ` +${offer.terms.countries.length - 3} more` : ''} ·{' '}
                                    {provider === 'both' ? 'Both stores' : customerStoreName(provider)} ·{' '}
                                    {offer.terms.environment === 'sandbox' ? 'Test' : 'Real'} transactions
                                </span>
                            </button>
                        ))}
                    </div>
                    {busy && <OfferNotice>Loading compatible offers…</OfferNotice>}
                    {!busy && !error && !compatible.length && (
                        <OfferNotice>
                            {search
                                ? 'No offers match this search.'
                                : `No verified ${customerPlanName(plan)} offers are available for ${provider === 'both' ? 'both stores' : customerStoreName(provider)}.`}
                        </OfferNotice>
                    )}
                    {error && (
                        <>
                            <OfferNotice error>{error}</OfferNotice>
                            <OfferButton variant="secondary" onClick={() => setReload((value) => value + 1)}>
                                Retry offers
                            </OfferButton>
                        </>
                    )}
                    {nextCursor && (
                        <OfferButton variant="secondary" disabled={busy} onClick={() => void loadMore()}>
                            Load more offers
                        </OfferButton>
                    )}
                </div>
            </details>
            <p className={customerOfferSecondaryClass}>Only active Gluon offers verified for this plan and store are listed.</p>
        </div>
    )
}
